import React, { useEffect, useId, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, ContentPaste, Delete } from '@mui/icons-material';
import type { DashboardFilter } from './dashboardConfig';
import { humanizeField } from './dashboardConfig';
import type { DashboardFilterOperator } from './dashboardFilters';
import {
    convertFilterValue,
    DASHBOARD_FILTER_OPERATOR_LABELS,
    filterOperatorOptions,
    filterValueToList,
    filterValueToText,
    isExactMatchOperator,
    isMultiValueOperator,
    isNegatedOperator,
    listToFilterValue,
    textToFilterValue,
    withOptionCasing,
} from './dashboardFilters';
import useFilterValueSuggestions, { offersValueSuggestions } from './useFilterValueSuggestions';
import { isCalcField } from './dashboardCalculations';
import type { DashboardRollingPeriod } from './dashboardRollingDates';
import { DASHBOARD_ROLLING_PERIODS } from './dashboardRollingDates';

// The condition picker's entry for a rolling date window; stored as `rolling` on the filter.
const ROLLING_OPERATOR = '__rolling';

// The value box keeps exactly what was typed and only parses it for the filter. Parsing on
// every keystroke and showing the result back trimmed the text as it was typed, so a space
// (or a comma in an "in" list) vanished before the next word could follow it.
const FilterValueField: React.FC<{
    filter: DashboardFilter;
    disabled?: boolean;
    onChange: (value: DashboardFilter['value']) => void;
}> = ({ filter, disabled, onChange }) => {
    const [text, setText] = useState(() => filterValueToText(filter.value));
    // Take the value from outside when it changes for another reason — an operator switch, a
    // paste, a loaded config — but not when it's just this box's own text, parsed.
    useEffect(() => {
        setText(current =>
            JSON.stringify(textToFilterValue(current, filter.operator)) ===
            JSON.stringify(filter.value)
                ? current
                : filterValueToText(filter.value),
        );
    }, [filter.value, filter.operator]);
    return (
        <TextField
            size="small"
            sx={{ flex: 1 }}
            disabled={disabled}
            placeholder="value"
            inputProps={{ 'aria-label': 'Value' }}
            value={text}
            onChange={e => {
                setText(e.target.value);
                onChange(textToFilterValue(e.target.value, filter.operator));
            }}
        />
    );
};

// The values of an include/exclude or text filter, as chips: picked from the field's own values
// (searched as you type), or typed and entered. Several values match any of them, or none of
// them for a negated operator. Pasting a column of values adds one chip per line.
const FilterValueChips: React.FC<{
    filter: DashboardFilter;
    disabled?: boolean;
    onChange: (value: DashboardFilter['value']) => void;
    source?: string;
    // The filters the suggestions are narrowed by.
    contextFilters: DashboardFilter[];
}> = ({ filter, disabled, onChange, source, contextFilters }) => {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const values = filterValueToList(filter.value);
    const { options, loading } = useFilterValueSuggestions({
        source,
        field: filter.field,
        search: input,
        filters: contextFilters,
        enabled: open && !disabled,
    });
    const commit = (list: string[]) =>
        onChange(
            listToFilterValue(
                isExactMatchOperator(filter.operator) ? withOptionCasing(list, options) : list,
                filter.operator,
            ),
        );
    const picked = new Set(values.map(v => v.toLowerCase()));
    return (
        <Autocomplete
            multiple
            freeSolo
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            disabled={disabled}
            options={options}
            value={values}
            inputValue={input}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            loading={loading}
            loadingText="Finding values…"
            // The server has already searched; filtering again would hide values whose match
            // isn't at the start of the label.
            filterOptions={opts => opts.filter(o => !picked.has(o.toLowerCase()))}
            noOptionsText={
                offersValueSuggestions(source, filter.field)
                    ? 'No matching values — press Enter to use what you typed'
                    : 'Press Enter to add the value'
            }
            onChange={(_, next) => {
                commit(next as string[]);
                setInput('');
            }}
            onInputChange={(_, text, reason) => {
                if (reason !== 'reset') setInput(text);
            }}
            // Leaving the box with something typed keeps it rather than silently dropping it.
            onBlur={() => {
                if (input.trim()) commit([...values, input]);
                setInput('');
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={values.length ? '' : 'Pick or type values'}
                    inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Value',
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                            const text = e.clipboardData.getData('text');
                            if (!/[\r\n\t]/.test(text)) return;
                            e.preventDefault();
                            commit([...values, ...text.split(/[\r\n\t]+/)]);
                            setInput('');
                        },
                    }}
                />
            )}
        />
    );
};

// `between` takes a pair, edited as two boxes. Each bound is kept exactly as typed.
const BetweenValueFields: React.FC<{
    filter: DashboardFilter;
    disabled?: boolean;
    onChange: (value: DashboardFilter['value']) => void;
}> = ({ filter, disabled, onChange }) => {
    const [from = '', to = ''] = Array.isArray(filter.value)
        ? filter.value
        : [filterValueToText(filter.value)];
    return (
        <Stack direction="row" gap={1} alignItems="center" sx={{ flex: 1 }}>
            <TextField
                size="small"
                sx={{ flex: 1 }}
                disabled={disabled}
                placeholder="from"
                inputProps={{ 'aria-label': 'From' }}
                value={from}
                onChange={e => onChange([e.target.value, to])}
            />
            <Typography variant="body2" color="text.secondary">
                and
            </Typography>
            <TextField
                size="small"
                sx={{ flex: 1 }}
                disabled={disabled}
                placeholder="to"
                inputProps={{ 'aria-label': 'To' }}
                value={to}
                onChange={e => onChange([from, e.target.value])}
            />
        </Stack>
    );
};

// Reusable field/operator/value filter editor, shared by per-widget filters and the
// page-level global filters.
const DashboardFilterRows: React.FC<{
    filters: DashboardFilter[];
    onChange: (filters: DashboardFilter[]) => void;
    t: (k: string, o?: any) => string;
    // Fields of the source the filters run against: a datasource's synced schema, or the
    // static dynamic-report schema on the default report. The two disagree (`buyer` vs
    // `campaign_buyer`), so offering the wrong one builds filters the query can't apply.
    fields: string[];
    // Heading above the rows; '' for none.
    label?: string;
    // Show the filters as they'd be edited, but read-only — used where a widget displays the
    // filters it inherits from the dashboard.
    disabled?: boolean;
    // The source the filters run against — DASHBOARD_FILTER_SOURCE_DEFAULT or a datasource id —
    // which the value picker suggests values from. Without one it takes typed values only.
    source?: string;
    // Filters that apply alongside these (a widget's inherited global filters, say). They and
    // the other rows here narrow the suggested values to ones that can still match.
    inheritedFilters?: DashboardFilter[];
    // How a field is named in the picker; calculated fields have names of their own.
    labelOf?: (field: string) => string;
}> = ({
    filters,
    onChange,
    t,
    fields,
    label = 'Filters',
    disabled,
    source,
    inheritedFilters = [],
    labelOf,
}) => {
    const idPrefix = useId();
    const nameOf = labelOf ?? ((field: string) => humanizeField(field, t));
    // Keep any field an existing filter already uses, even when its source doesn't list it,
    // so a schema that hasn't loaded (or has changed) never silently blanks a saved filter.
    const fieldOptions = (current?: string): string[] => {
        const all = current && !fields.includes(current) ? [...fields, current] : fields;
        return [...all].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    };
    const newFilterFields = fieldOptions();

    // Pasting a column of ids beats typing them comma by comma, so each row can be filled
    // from a block of text, one value per line.
    const [pasteRow, setPasteRow] = useState<number | null>(null);
    const [pasteText, setPasteText] = useState('');
    const pastedValues = useMemo(
        () => [
            ...new Set(
                pasteText
                    .split(/\r?\n/)
                    .map(v => v.trim())
                    .filter(Boolean),
            ),
        ],
        [pasteText],
    );
    const openPaste = (i: number) => {
        const { value } = filters[i];
        setPasteText(
            Array.isArray(value) ? value.join('\n') : value === '' ? '' : String(value ?? ''),
        );
        setPasteRow(i);
    };
    const applyPaste = () => {
        if (pasteRow == null) return;
        onChange(
            filters.map((x, j) => {
                if (j !== pasteRow) return x;
                // A list only means anything to a multi-value operator, so move a single-value
                // one across rather than silently keeping a comparison it can't make.
                const operator: DashboardFilterOperator = isMultiValueOperator(x.operator)
                    ? x.operator
                    : isNegatedOperator(x.operator)
                    ? 'not_in'
                    : 'in';
                return { ...x, operator, value: listToFilterValue(pastedValues, operator) };
            }),
        );
        setPasteRow(null);
    };
    return (
        <Box>
            {(label !== '' || !disabled) && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">{label}</Typography>
                    {!disabled && (
                        <Button
                            size="small"
                            startIcon={<Add fontSize="small" />}
                            disabled={newFilterFields.length === 0}
                            onClick={() =>
                                onChange([
                                    ...filters,
                                    { field: newFilterFields[0], operator: 'eq', value: '' },
                                ])
                            }
                        >
                            Add filter
                        </Button>
                    )}
                </Stack>
            )}
            {filters.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Stack direction="row" gap={1} sx={{ mt: 1 }} key={i} alignItems="center">
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel id={`${idPrefix}-field-${i}`}>Field</InputLabel>
                        <Select
                            labelId={`${idPrefix}-field-${i}`}
                            label="Field"
                            disabled={disabled}
                            value={f.field}
                            onChange={e =>
                                onChange(
                                    filters.map((x, j) =>
                                        j === i ? { ...x, field: e.target.value } : x,
                                    ),
                                )
                            }
                        >
                            {fieldOptions(f.field).map(field => (
                                <MenuItem key={field} value={field}>
                                    {nameOf(field)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ width: 170 }}>
                        <InputLabel id={`${idPrefix}-condition-${i}`}>Condition</InputLabel>
                        <Select
                            labelId={`${idPrefix}-condition-${i}`}
                            label="Condition"
                            disabled={disabled}
                            value={f.rolling ? ROLLING_OPERATOR : f.operator}
                            onChange={e => {
                                const picked = e.target.value as string;
                                onChange(
                                    filters.map((x, j) => {
                                        if (j !== i) return x;
                                        // A rolling window replaces the comparison: its dates
                                        // are worked out each time the dashboard loads.
                                        if (picked === ROLLING_OPERATOR) {
                                            return {
                                                field: x.field,
                                                ...(x.source ? { source: x.source } : {}),
                                                operator: 'gte' as const,
                                                value: '',
                                                rolling: x.rolling ?? 'last_7_days',
                                            };
                                        }
                                        const operator = picked as DashboardFilterOperator;
                                        const { rolling: _dropped, ...rest } = x;
                                        return {
                                            ...rest,
                                            operator,
                                            value: convertFilterValue(x.value, operator),
                                        };
                                    }),
                                );
                            }}
                        >
                            {filterOperatorOptions(f.operator).map(op => (
                                <MenuItem key={op} value={op}>
                                    {DASHBOARD_FILTER_OPERATOR_LABELS[op]}
                                </MenuItem>
                            ))}
                            {!isCalcField(f.field) && (
                                <MenuItem value={ROLLING_OPERATOR}>
                                    {t('Pages.DashboardHome.custom.rolling.operator')}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    {f.rolling ? (
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel id={`${idPrefix}-period-${i}`}>
                                {t('Pages.DashboardHome.custom.rolling.period')}
                            </InputLabel>
                            <Select
                                labelId={`${idPrefix}-period-${i}`}
                                label={t('Pages.DashboardHome.custom.rolling.period')}
                                disabled={disabled}
                                value={f.rolling}
                                onChange={e =>
                                    onChange(
                                        filters.map((x, j) =>
                                            j === i
                                                ? {
                                                      ...x,
                                                      rolling: e.target
                                                          .value as DashboardRollingPeriod,
                                                  }
                                                : x,
                                        ),
                                    )
                                }
                            >
                                {DASHBOARD_ROLLING_PERIODS.map(period => (
                                    <MenuItem key={period} value={period}>
                                        {t(`Pages.DashboardHome.custom.rolling.periods.${period}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : f.operator === 'between' ? (
                        <BetweenValueFields
                            filter={f}
                            disabled={disabled}
                            onChange={value =>
                                onChange(filters.map((x, j) => (j === i ? { ...x, value } : x)))
                            }
                        />
                    ) : isMultiValueOperator(f.operator) ? (
                        <FilterValueChips
                            filter={f}
                            disabled={disabled}
                            source={source}
                            contextFilters={[
                                ...inheritedFilters,
                                ...filters.filter((_, j) => j !== i),
                            ]}
                            onChange={value =>
                                onChange(filters.map((x, j) => (j === i ? { ...x, value } : x)))
                            }
                        />
                    ) : (
                        <FilterValueField
                            filter={f}
                            disabled={disabled}
                            onChange={value =>
                                onChange(filters.map((x, j) => (j === i ? { ...x, value } : x)))
                            }
                        />
                    )}
                    {!disabled && !f.rolling && (
                        <Tooltip title="Paste a list of values, one per line">
                            <IconButton size="small" onClick={() => openPaste(i)}>
                                <ContentPaste fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        size="small"
                        sx={{ visibility: disabled ? 'hidden' : 'visible' }}
                        disabled={disabled}
                        aria-label="Remove filter"
                        onClick={() => onChange(filters.filter((_, j) => j !== i))}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
            <Dialog
                open={pasteRow !== null}
                onClose={() => setPasteRow(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Paste values</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" color="text.secondary">
                        One value per line — paste a column of ids straight in. Blank lines and
                        repeats are dropped, and the filter switches to matching any of them.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={8}
                        maxRows={16}
                        size="small"
                        sx={{ mt: 1 }}
                        value={pasteText}
                        onChange={e => setPasteText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={() => setPasteRow(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={pastedValues.length === 0}
                        onClick={applyPaste}
                    >
                        {pastedValues.length === 1
                            ? 'Use 1 value'
                            : `Use ${pastedValues.length} values`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DashboardFilterRows;
